// @ts-nocheck
import { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { CoreUtils } from '../utils/Utils';
import { API_ENDPOINTS } from '../const/ApiEndpoints';
import constants from '../const/Const';
import { selectAuth } from '../state/slices/appSlice';
import { startCase } from 'lodash';

const initialStackChartConf = {
  levels: ['Year', 'Month', 'Week', 'Day'],
  activeLevelIndex: 0,
};
const initialLineChartConf = {
  levels: ['Year', 'month', 'week'],
  activeLevelIndex: 0,
};
/**
 * @function useDashBoard
 * @description a custom hook which handles APIs related to dashboard screen
 * @returns {object} object
 */
export default function useDashBoard() {
  const [stats, setStats] = useState([]);
  const [stackChartData, setStackChartData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [teamList, setTeamList] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [searchData, setSearchData] = useState([]);
  const [analystType, setAnalystType] = useState([]);
  const [historyPageCount, setHistoryPageCount] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector(selectAuth);
  const {
    mergeArrays,
    getFailedTypeMessage,
    lastElement,
    getDetailsFieldNames,
    mergeByField,
  } = CoreUtils;
  const { enqueueSnackbar } = useSnackbar();

  // stack chart variables
  const [stackChartConf, setStackChartConfig] = useState(initialStackChartConf);
  // line chart variables
  const [lineChartConf, setLineChartConfig] = useState(initialLineChartConf);
  // const User History Data (Summary)
  const [userHistorySummary, setUserHistorySummary] = useState([
    {
      details: [],
      sanctionsList: [],
      sanctionsCountryList: [],
      sanctionsMnrlList: [],
      peplist: [],
      amsList: [],
    },
  ]);

  /**
   * @function fetchStats
   * @description A function to fetch dashboard stats
   * @returns {Promise} returns an axios promise
   */
  const fetchStats = (queryParams) => {
    setStackChartConfig(initialStackChartConf);
    setLineChartConfig(initialLineChartConf);
    setStats([]);
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.DASHBOARD_STATS, {
        params: Object.fromEntries(
          Object.entries(queryParams).map(([key, value]) =>
            Array.isArray(value) ? [key, value?.join(',')] : [key, value]
          )
        ),
      })
      .then(
        (response) => {
          setIsLoading(false);
          if (response?.data) {
            setStats(response?.data);
          }
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };
  /**
   * @function fetchHistory
   * @description A function to fetch user's history
   * @returns {Promise} returns an axios promise
   */

  const fetchHistory = (queryParams) => {
    setIsLoading(true);
    setReviewHistory([]);
    const updatedParams = { ...queryParams };

    if (updatedParams.username?.length) {
      const usernameToId = {};
      teamList.forEach((team) => {
        team.analysts?.forEach((analyst) => {
          usernameToId[analyst.username] = analyst.id;
        });
      });

      const usernameArray = Array.isArray(updatedParams.username)
        ? updatedParams.username
        : updatedParams.username.split(',');

      updatedParams.selectedUsers = usernameArray
        .map((uname) => usernameToId[uname.trim()])
        .filter(Boolean);
    }

    if (updatedParams.alerted?.length === 2) {
      delete updatedParams.alerted;
    }
    const params = Object.fromEntries(
      Object.entries(updatedParams).map(([key, value]) =>
        Array.isArray(value) ? [key, value?.join(',')] : [key, value]
      )
    );
    const isTransactionModule = updatedParams?.module === 'transaction_screen';
    return axios
      .get(
        isTransactionModule
          ? API_ENDPOINTS.SEARCH_HISTORY_DATA
          : API_ENDPOINTS.REVIEW_HISTORY,
        { params }
      )

      .then(
        (response) => {
          setHistoryPageCount(response?.headers['x-total-count']);
          setIsLoading(false);
          if (response?.data) {
            setReviewHistory(response?.data);
          }
          return response;
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };
  /**
   * @function userTypes
   * @description A function to fetch user's type
   * @returns {Promise} returns an axios promise
   */
  const userTypes = (queryParams) => {
    return axios
      .get(API_ENDPOINTS.DASHBOARD_USER_TYPE, {
        params: queryParams,
      })
      .then(
        (response) => {
          if (response.length !== 0) {
            setAnalystType(response.data);
          }
        },
        () => {}
      );
  };
  /**
   * @function fetchTeamData
   * @description A function to fetch team data
   * @returns {Promise} returns an axios promise
   */
  const fetchTeamData = (queryParams) => {
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.INITIATE_SCREEN_TEAM_DATA, {
        params: queryParams,
      })
      .then(
        (response) => {
          setIsLoading(false);
          setTeamList(response?.data);
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };

  const fetchBaseTableData = ({ page = 0, pageSize = 5, filters }) => {
    const startDate =
      filters?.StartDate ||
      (filters?.dateReset?.start
        ? moment(filters.dateReset.start).format(constants.GLOBAL_DATE_FORMAT)
        : '');
    const endDate =
      filters?.EndDate ||
      (filters?.dateReset?.end
        ? moment(filters.dateReset.end).format(constants.GLOBAL_DATE_FORMAT)
        : '');

    const params = {
      page: page + 1 || '',
      pagesize: pageSize || '',
      ...(['admin']?.includes(filters?.user_type) &&
        filters?.manager_id && {
          manager_id: Array.isArray(filters.manager_id)
            ? filters.manager_id.join(',')
            : filters.manager_id,
        }),
      ...(filters?.analyst_id && { analyst_id: filters.analyst_id }),
      screen_type: filters?.screen_type,
      trxn_dashboard: filters?.module === 'transaction_screen',
      ...(startDate ? { StartDate: startDate } : {}),
      ...(endDate ? { EndDate: endDate } : {}),
    };
    return axios
      .get(API_ENDPOINTS.DASHBOARD_BATCH_INFO, {
        params,
        loader: false,
      })
      .then((res) => {
        return {
          data: res.data,
          totalRowCount: res.headers?.['x-total-count'],
        };
      });
  };
  /**
   * @function fetchStackChartDataForDrilldown
   * @description A function to fetch data of stack chart for drill down
   * @returns {Promise} returns an axios promise
   */
  const fetchStackChartDataForDrilldown = (queryParams, chartData) => {
    if (stackChartConf.activeLevelIndex < 3) {
      const nextActiveIndex = stackChartConf.activeLevelIndex + 1;
      const nextActiveLevel = stackChartConf.levels[nextActiveIndex];
      const activeLevel =
        stackChartConf.levels[stackChartConf.activeLevelIndex];
      const decidingParameter =
        chartData !== undefined ? nextActiveLevel.toLowerCase() : 'year';

      const year =
        chartData !== undefined
          ? activeLevel === 'Year'
            ? chartData.indexValue
            : stackChartConf.year
          : null;
      const month =
        chartData !== undefined
          ? activeLevel === 'Month'
            ? chartData.indexValue
            : stackChartData?.stacked_chart?.Week?.[0]?.id
            ? stackChartData?.stacked_chart?.Week?.[0]?.id
            : ''
          : null;
      const week =
        chartData !== undefined
          ? activeLevel === 'Week'
            ? chartData.indexValue.split('_')[1]
            : ''
          : null;
      // eslint-disable-next-line no-param-reassign
      queryParams = {
        decidingParameter,
        year,
        month: month ? moment().month(month).format('M') : '',
        week,
        ...queryParams,
      };

      setIsLoading(true);
      return axios
        .get(API_ENDPOINTS.DASHBOARD_STACK_CHART_DRILLDOWN, {
          params: Object.fromEntries(
            Object.entries(queryParams).map(([key, value]) =>
              Array.isArray(value) ? [key, value?.join(',')] : [key, value]
            )
          ),
        })
        .then(
          (response) => {
            setIsLoading(false);
            if (chartData !== undefined) {
              setStackChartData({
                stacked_chart: {
                  ...stackChartData.stacked_chart,
                  [nextActiveLevel]: response.data[nextActiveLevel],
                },
              });
              const newConf = {
                ...stackChartConf,
                activeLevelIndex: nextActiveIndex,
              };
              if (activeLevel === 'Year') {
                newConf.year = chartData.indexValue;
              }
              setStackChartConfig(newConf);
            } else {
              setStackChartData({
                stacked_chart: {
                  ...response?.data,
                },
              });
            }
          },
          () => {
            setIsLoading(false);
          }
        );
    }
  };
  /**
   * @function fetchLineChartDataForDrilldown
   * @description A function to fetch data of linr chart for drill down
   * @returns {Promise} returns an axios promise
   */
  const fetchLineChartDataForDrilldown = (queryParams, chartData) => {
    if (lineChartConf.activeLevelIndex < 3) {
      const nextActiveIndex = lineChartConf.activeLevelIndex + 1;
      const nextActiveLevel = lineChartConf.levels[nextActiveIndex];
      const activeLevel = lineChartConf.levels[lineChartConf.activeLevelIndex];
      const decidingParameter =
        chartData !== undefined ? nextActiveLevel.toLowerCase() : 'year';

      const year =
        chartData !== undefined
          ? activeLevel === 'Year'
            ? chartData.data.x
            : lineChartData.target_miss.month?.[0]?.id
          : null;
      const month =
        chartData !== undefined
          ? activeLevel === 'month'
            ? chartData.data.x
            : lineChartData.target_miss.week?.[0]?.id
            ? lineChartData.target_miss.week?.[0]?.id
            : ''
          : null;
      const week =
        chartData !== undefined
          ? activeLevel === 'week'
            ? chartData.data.x.split('_')[1]
            : ''
          : null;
      // eslint-disable-next-line no-param-reassign
      queryParams = {
        decidingParameter,
        year,
        month: month ? moment().month(month).format('M') : '',
        week,
        ...queryParams,
      };

      setIsLoading(true);
      return axios
        .get(API_ENDPOINTS.DASHBOARD_LINE_CHART_DRILLDOWN, {
          params: Object.fromEntries(
            Object.entries(queryParams).map(([key, value]) =>
              Array.isArray(value) ? [key, value?.join(',')] : [key, value]
            )
          ),
        })
        .then(
          (response) => {
            setIsLoading(false);
            if (chartData !== undefined) {
              setLineChartData({
                target_miss: {
                  ...lineChartData.target_miss,
                  [nextActiveLevel]: response.data[nextActiveLevel],
                },
              });
              setLineChartConfig({
                ...lineChartConf,
                activeLevelIndex: nextActiveIndex,
              });
            } else {
              setLineChartData({
                target_miss: {
                  ...response?.data,
                },
              });
            }
          },
          () => {
            setIsLoading(false);
          }
        );
    }
  };
  /**
   * @function fetchUserHistory
   * @description A function to fetch user's history
   * @returns {Promise} returns an axios promise
   */
  const fetchUserHistory = (
    encryptedKey,
    baseEncryptedKey,
    screeningType,
    userLevel
  ) => {
    setIsLoading(true);
    const body = {
      search_id: encryptedKey,
      rank:
        userLevel === -1 ? (baseEncryptedKey === 'maker' ? 0 : 1) : userLevel,
      screen_type: screeningType === 'InitialScreening' ? 'initial' : 'ongoing',
    };
    return axios.post(API_ENDPOINTS.DASHBOARD_USER_HISTORY, body).then(
      (response) => {
        setIsLoading(false);

        const sanctionList = response?.data?.result?.sanction_result?.map(
          (item) => ({
            ...item,
            resultType: 'sanctions',
          })
        );
        const sanctionCountryList =
          response?.data?.result?.sanction_country_result?.map((item) => ({
            ...item,
            resultType: 'sanctionsCountry',
          }));
        const mnrlResult = response?.data?.result?.mnrl_result?.map((item) => ({
          ...item,
          resultType: 'sanctionsMnrl',
        }));

        setUserHistorySummary({
          details: {
            ...response?.data,
            result: {
              ...response?.data?.result,
              sanction_result: sanctionList,
              sanction_country_result: sanctionCountryList,
              mnrl_result: mnrlResult,
            },
          },
          sanctionsList: sanctionList,
          sanctionsCountryList: sanctionCountryList,
          sanctionsMnrlList: mnrlResult,

          pepList: response?.data?.result?.pep_result,
          amsList: response?.data?.result?.ams_result,
          legalList: response?.data?.result?.legal_result,
          corporateList: response?.data?.result?.corporate_result,
          riskList: lastElement(response?.data?.result?.risk_rating_results)
            ?.risk_rating_result,
          referralData: response?.data?.referrals,
        });
        if (response?.data?.failed_type) {
          const message = getFailedTypeMessage(response.data.failed_type);
          if (message) {
            enqueueSnackbar(message, {
              variant: 'warning',
            });
          }
        }
      },
      (e) => {
        setIsLoading(false);
        setIsError(e);
      }
    );
  };

  /**
   * @function fetchUserTransactionHistory
   * @description A function to fetch user's history
   * @returns {Promise} returns an axios promise
   */
  const fetchUserTransactionHistory = (
    encryptedKey,
    baseEncryptedKey,
    screeningType,
    userLevel
  ) => {
    setIsLoading(true);
    const body = {
      internal_transaction_id: encryptedKey,
      rank:
        userLevel === -1 ? (baseEncryptedKey === 'maker' ? 0 : 1) : userLevel,
      screen_type: screeningType === 'InitialScreening' ? 'initial' : 'ongoing',
    };
    return axios
      .get(API_ENDPOINTS.DASHBOARD_USER_TRANSACTION_HISTORY, {
        params: body,
      })
      .then(
        (response) => {
          setIsLoading(false);

          const data = response?.data || {};
          const partyKeys = getDetailsFieldNames(data);

          const allSanctions = mergeByField(
            data,
            partyKeys,
            'sanction_result',
            'sanctions'
          );
          const allSanctionCountry = mergeByField(
            data,
            partyKeys,
            'sanction_country_result',
            'sanctionsCountry'
          );
          const allMnrl = mergeByField(
            data,
            partyKeys,
            'mnrl_result',
            'sanctionsMnrl'
          );
          const allPep = mergeByField(data, partyKeys, 'pep_result', 'pep');
          const allAms = mergeByField(data, partyKeys, 'ams_result', 'ams');
          const allLegal = mergeByField(
            data,
            partyKeys,
            'legal_result',
            'legal'
          );
          const allCorporate = mergeByField(
            data,
            partyKeys,
            'corporate_result',
            'corporate'
          );

          const partyAlertSections = partyKeys.map((partyKey) => {
            const party = partyKey.replace('_details', '');

            return {
              key: party,
              field: party,
              label: startCase(party),

              sanctionsList: allSanctions.filter((i) => i.party === party),
              sanctionsCountryList: allSanctionCountry.filter(
                (i) => i.party === party
              ),
              sanctionsMnrlList: allMnrl.filter((i) => i.party === party),
              pepList: allPep.filter((i) => i.party === party),
              amsList: allAms.filter((i) => i.party === party),
              legalList: allLegal.filter((i) => i.party === party),
              corporateList: allCorporate.filter((i) => i.party === party),
            };
          });
          setUserHistorySummary({
            details: {
              ...data,
              initiated_by_fullname: (() => {
                for (let key of partyKeys) {
                  const entity = Object.values(data?.[key] || {}).find(
                    (e) => e?.initiated_by_fullname
                  );
                  if (entity) return entity.initiated_by_fullname;
                }
                return '';
              })(),
              initiated_by_username: (() => {
                for (let key of partyKeys) {
                  const entity = Object.values(data?.[key] || {}).find(
                    (e) => e?.initiated_by_username
                  );
                  if (entity) return entity.initiated_by_username;
                }
                return '';
              })(),
              result: {
                sanction_result: allSanctions,
                sanction_country_result: allSanctionCountry,
                mnrl_result: allMnrl,
                pep_result: allPep,
                ams_result: allAms,
                legal_result: allLegal,
                corporate_result: allCorporate,

                remarks: (() => {
                  for (let key of partyKeys) {
                    const partyObj = data?.[key];
                    const remarks = Object.values(partyObj || {}).flatMap(
                      (e) => e?.result?.remarks || []
                    );

                    if (remarks.length) return remarks;
                  }
                  return [];
                })(),
              },
            },

            partyAlertSections,

            groupedByParty: Object.fromEntries(
              partyAlertSections.map((p) => [p.key, p])
            ),
            sanctionsList: allSanctions,
            sanctionsCountryList: allSanctionCountry,
            sanctionsMnrlList: allMnrl,
            pepList: allPep,
            amsList: allAms,
            legalList: allLegal,
            corporateList: allCorporate,

            referralData: data?.referrals,
          });
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };
  // const availableSearch = () => {
  //   setIsLoading(true);

  //   return axios.get(API_ENDPOINTS.DASHBOARD_AVAILABLE_SEARCHES).then(
  //     (response) => {
  //       setIsLoading(false);
  //       setSearchData(response.data);
  //     },
  //     (e) => {
  //       setIsLoading(false);
  //       setIsError(e);
  //     }
  //   );
  // };
  /**
   * @function levelUpStackChart
   * @description A function for setting the stack chart configuration
   * @returns {void}
   */
  const levelUpStackChart = () => {
    if (stackChartConf.activeLevelIndex > 0 <= 3) {
      setStackChartConfig({
        ...stackChartConf,
        activeLevelIndex: stackChartConf.activeLevelIndex - 1,
      });
    }
  };
  /**
   * @function levelUpLineChart
   * @description A function for setting the line chart configuration
   * @returns {void}
   */
  const levelUpLineChart = () => {
    if (lineChartConf.activeLevelIndex > 0 <= 3) {
      setLineChartConfig({
        ...lineChartConf,
        activeLevelIndex: lineChartConf.activeLevelIndex - 1,
      });
    }
  };
  return {
    user,
    isError,
    isLoading,
    stats,
    fetchStats,
    userTypes,
    analystType,
    fetchStackChartDataForDrilldown,
    levelUpStackChart,
    stackChartConf,
    fetchLineChartDataForDrilldown,
    lineChartConf,
    levelUpLineChart,
    fetchUserHistory,
    fetchUserTransactionHistory,
    userHistorySummary,
    // availableSearch,
    searchData,
    fetchHistory,
    reviewHistory,
    stackChartData,
    lineChartData,
    historyPageCount,
    fetchTeamData,
    teamList,
    fetchBaseTableData,
  };
}
